import React, { useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Upload, Loader2, FileText, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';

export const CATEGORIES = [
  'Arbeitsvertrag',
  'Bescheinigung',
  'Zeugnis',
  'Kündigung',
  'Abrechnung',
  'Allgemein',
];

export const UploadTemplateDialog = ({ open, onOpenChange, uploadForm, setUploadForm, onUpload, uploading }) => {
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const ext = file.name.split('.').pop().toLowerCase();
      if (!['docx', 'xlsx'].includes(ext)) {
        toast.error('Nur .docx und .xlsx Dateien erlaubt');
        return;
      }
      setUploadForm((prev) => ({
        ...prev,
        file,
        name: prev.name || file.name.replace(/\.(docx|xlsx)$/, ''),
      }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Upload className="h-5 w-5 text-primary" />
            Vorlage hochladen
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label htmlFor="template-name">Name *</Label>
            <Input
              id="template-name"
              value={uploadForm.name}
              onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
              placeholder="z.B. Arbeitsvertrag"
              data-testid="template-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Kategorie</Label>
            <Select
              value={uploadForm.category}
              onValueChange={(v) => setUploadForm({ ...uploadForm, category: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="template-desc">Beschreibung</Label>
            <Textarea
              id="template-desc"
              value={uploadForm.description}
              onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
              placeholder="Optionale Beschreibung..."
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label>Datei (.docx oder .xlsx) *</Label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".docx,.xlsx"
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:bg-secondary/30 transition-colors"
            >
              {uploadForm.file ? (
                <div className="flex items-center justify-center gap-2">
                  {uploadForm.file.name.endsWith('.docx') ? (
                    <FileText className="h-8 w-8 text-blue-500" />
                  ) : (
                    <FileSpreadsheet className="h-8 w-8 text-green-500" />
                  )}
                  <div className="text-left">
                    <p className="font-medium">{uploadForm.file.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {(uploadForm.file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">
                    Klicken zum Auswählen oder Datei hierher ziehen
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    .docx oder .xlsx (max. 10MB)
                  </p>
                </>
              )}
            </button>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">
              Abbrechen
            </Button>
            <Button onClick={onUpload} disabled={uploading} className="rounded-full">
              {uploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Wird hochgeladen...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Hochladen
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
